import React, { useCallback, useEffect, useState } from 'react';
import { ArrowUpDown, Ban, Crown, Eye, LogOut, Radio, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import Loader from '../Loader';
import { Badge, Confirm, Empty, HScroll, Pager, Status } from './UpUi';
import { ago, date, duration } from './format';

const SORTS = [
    ['createdAt', 'Inscription'],
    ['week', 'Les plus actifs (7 jours)'],
    ['sessionAt', 'Dernière connexion'],
    ['email', 'Email'],
    ['premiumUntil', 'Fin du premium'],
    ['warnings', 'Avertissements'],
    ['id', 'ID'],
]

/** Actions possibles sur plusieurs users à la fois (POST /users/bulk). */
const BULK = [
    { action: 'block', label: 'Bloquer', icon: Ban, danger: true, text: (n) => `Bloquer ${n} compte(s) ? Ils sont coupés tout de suite (vidéo arrêtée) et ne peuvent plus se connecter.` },
    { action: 'unblock', label: 'Débloquer', icon: ShieldCheck, text: (n) => `Débloquer ${n} compte(s) ?` },
    { action: 'logout', label: 'Déconnecter', icon: LogOut, text: (n) => `Déconnecter ${n} compte(s) de tous leurs appareils ?` },
    { action: 'premium', label: 'Premium +7 j', icon: Crown, body: { days: 7 }, text: (n) => `Ajouter 7 jours de premium à ${n} compte(s) ?` },
    { action: 'premium', label: '+30 j', icon: Crown, body: { days: 30 }, text: (n) => `Ajouter 30 jours de premium à ${n} compte(s) ?` },
    { action: 'premium', label: 'Retirer premium', icon: Crown, body: { until: null }, danger: true, text: (n) => `Retirer le premium de ${n} compte(s) ?` },
]

const PremiumCell = ({ u }) => {
    if (!u.premiumUntil) return <span className='up-muted'>Gratuit</span>
    if (!u.premium) return <span className='up-muted' title={`expiré le ${date(u.premiumUntil)}`}>Expiré · {date(u.premiumUntil)}</span>
    return <Badge tone='gold'>Premium · {date(u.premiumUntil)}</Badge>
}

/** Onglet Users : liste paginée côté serveur, filtres, tri, sélection et actions en lot. */
const UpUsers = ({ pApi, onOpen, notify, reloadKey, onChanged }) => {
    const [filters, setFilters] = useState({ q: '', status: '', plan: '', warned: '', online: '', sort: 'createdAt', dir: 'desc', perPage: 25 })
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [selected, setSelected] = useState({})
    const [confirm, setConfirm] = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const params = Object.fromEntries(Object.entries({ ...filters, page }).filter(([, v]) => v !== ''))
            setData(await pApi.get('/users', params))
        } catch (e) {
            setError(e.message)
        }
        setLoading(false)
    }, [pApi, filters, page])

    useEffect(() => { load() }, [load, reloadKey])

    // recherche : 350 ms après la dernière touche
    useEffect(() => {
        const t = setTimeout(() => { setFilters((f) => (f.q === search.trim() ? f : { ...f, q: search.trim() })); setPage(1) }, 350)
        return () => clearTimeout(t)
    }, [search])

    // valeur lue TOUT DE SUITE : dans la mise à jour (exécutée plus tard par React), e.currentTarget est déjà null
    const set = (key) => (e) => {
        const { type, checked, value } = e.currentTarget
        const v = type === 'checkbox' ? (checked ? '1' : '') : value
        setFilters((f) => ({ ...f, [key]: v }))
        setPage(1)
    }

    const items = data?.items || []
    const ids = Object.keys(selected).filter((id) => selected[id]).map(Number)
    const allSel = items.length > 0 && items.every((u) => selected[u.id])
    const toggleAll = () => setSelected(allSel ? {} : Object.fromEntries(items.map((u) => [u.id, true])))

    const runBulk = (b) => setConfirm({
        title: b.label,
        text: b.text(ids.length),
        danger: b.danger,
        action: b.label,
        onConfirm: async () => {
            try {
                const r = await pApi.post('/users/bulk', { ids, action: b.action, ...(b.body || {}) })
                const failed = r.results.filter((x) => !x.ok)
                notify(failed.length ? `${r.done} fait(s), ${failed.length} en erreur : ${failed[0].error}` : `${b.label} : ${r.done} compte(s)`, failed.length > 0)
                setSelected({})
                load()
                onChanged()
            } catch (e) {
                notify(e.message, true)
            }
        },
    })

    const quick = (u, action) => setConfirm({
        title: action === 'block' ? 'Bloquer ce compte' : 'Débloquer ce compte',
        text: action === 'block' ? `Bloquer ${u.email} ? Il est coupé tout de suite et ne peut plus se connecter.` : `Débloquer ${u.email} ?`,
        danger: action === 'block',
        action: action === 'block' ? 'Bloquer' : 'Débloquer',
        onConfirm: async () => {
            try {
                await pApi.post(`/users/${u.id}/${action}`)
                notify(`${u.email} : ${action === 'block' ? 'bloqué' : 'débloqué'}`)
                load()
                onChanged()
            } catch (e) {
                notify(e.message, true)
            }
        },
    })

    return (
        <div className='up-users'>
            <div className='up-toolbar'>
                <div className='up-search'>
                    <Search size={16} />
                    <input placeholder='Email ou ID…' value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
                </div>
                <select value={filters.status} onChange={set('status')}>
                    <option value=''>Statut : tous</option>
                    <option value='ACTIVE'>Actifs</option>
                    <option value='BLOCKED'>Bloqués</option>
                    <option value='SUSPENDED'>Bloqués temporairement</option>
                </select>
                <select value={filters.plan} onChange={set('plan')}>
                    <option value=''>Offre : toutes</option>
                    <option value='premium'>Premium</option>
                    <option value='free'>Gratuit</option>
                </select>
                <select value={filters.warned} onChange={set('warned')}>
                    <option value=''>Avertissements : tous</option>
                    <option value='1'>Avec avertissements</option>
                </select>
                <label className='up-check'>
                    <input type='checkbox' checked={filters.online === '1'} onChange={set('online')} /> En ligne
                </label>
                <select value={filters.sort} onChange={set('sort')} title='Trier par'>
                    {SORTS.map(([v, l]) => <option key={v} value={v}>Tri : {l}</option>)}
                </select>
                <button className='btn cw up-icon-btn' title={filters.dir === 'desc' ? 'Décroissant' : 'Croissant'} onClick={() => setFilters((f) => ({ ...f, dir: f.dir === 'desc' ? 'asc' : 'desc' }))}>
                    <ArrowUpDown size={15} /> {filters.dir === 'desc' ? '↓' : '↑'}
                </button>
                <select value={filters.perPage} onChange={set('perPage')} title='Par page'>
                    {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <button className='btn cw up-icon-btn' onClick={load} title='Actualiser'><RefreshCw size={15} className={loading ? 'up-spin' : ''} /></button>
            </div>

            {ids.length > 0 && (
                <div className='up-bulk'>
                    <span><b>{ids.length}</b> sélectionné(s)</span>
                    <div className='up-bulk-actions'>
                        {BULK.map((b) => (
                            <button key={b.label} className={`btn ${b.danger ? 'up-danger-soft' : 'cw'}`} onClick={() => runBulk(b)}>
                                <b.icon size={14} /> {b.label}
                            </button>
                        ))}
                        <button className='btn cw' onClick={() => setSelected({})}>Tout désélectionner</button>
                    </div>
                </div>
            )}

            {error && <p className='up-error'>{error}</p>}
            {!data && !error ? (
                <div className='up-loading'><Loader /></div>
            ) : data && (
                <>
                    <p className='up-count'><b>{data.total}</b> user{data.total > 1 ? 's' : ''}{data.pages > 1 ? ` · page ${data.page} / ${data.pages}` : ''}</p>
                    <HScroll>
                        <div className='up-table up-table-users'>
                            <div className='up-thead'>
                                <span><input type='checkbox' checked={allSel} onChange={toggleAll} /></span>
                                <span>User</span><span>Statut</span><span>Offre</span><span>Inscrit</span><span>Dernière connexion</span>
                                <span className='tar' title='Temps regardé ces 7 derniers jours'>7 jours</span><span className='tac'>Profils</span><span className='tac'>Avert.</span><span className='tac'>Débloq.</span><span>Parrain</span><span className='tar'>Actions</span>
                            </div>
                            {items.map((u) => (
                                <div key={u.id} className={`up-row ${selected[u.id] ? 'sel' : ''}`} onClick={() => onOpen(u.id)}>
                                    <span onClick={(e) => e.stopPropagation()}>
                                        <input type='checkbox' checked={!!selected[u.id]} onChange={() => setSelected((s) => ({ ...s, [u.id]: !s[u.id] }))} />
                                    </span>
                                    <span className='up-user'>
                                        {u.online && <Radio size={13} className='up-live' title='En ligne' />}
                                        <span className='truncate' title={u.email}>{u.email}</span>
                                        <small>#{u.id}</small>
                                    </span>
                                    <span><Status status={u.status} suspendedUntil={u.suspendedUntil} /></span>
                                    <span><PremiumCell u={u} /></span>
                                    <span title={date(u.createdAt)}>{date(u.createdAt)}</span>
                                    <span className='up-session'>
                                        <span title={u.sessionAt || ''}>{u.sessionAt ? ago(u.sessionAt) : '—'}</span>
                                        <small className='truncate' title={`${u.device || ''} ${u.sessionIp || ''}`}>{[u.device, u.sessionIp].filter(Boolean).join(' · ') || '—'}</small>
                                    </span>
                                    <span className='tar' title='Temps regardé ces 7 derniers jours'>{u.week ? <b>{duration(u.week)}</b> : <span className='up-muted'>—</span>}</span>
                                    <span className='tac'>{u.profiles}</span>
                                    <span className='tac'>{u.warnings ? <Badge tone='warn'>{u.warnings}</Badge> : <span className='up-muted'>0</span>}</span>
                                    <span className='tac'>{u.unlocks}</span>
                                    <span className='truncate up-muted' title={u.invitedBy?.email || 'admin'}>{u.invitedBy?.email || 'admin'}</span>
                                    <span className='up-row-actions' onClick={(e) => e.stopPropagation()}>
                                        <button className='pay-act-btn' title='Voir la fiche' onClick={() => onOpen(u.id)}><Eye size={16} color='#fff' /></button>
                                        {u.status === 'BLOCKED' || u.suspended ? (
                                            <button className='pay-act-btn' title='Débloquer' onClick={() => quick(u, 'unblock')}><ShieldCheck size={16} color='#1ab338' /></button>
                                        ) : (
                                            <button className='pay-act-btn' title='Bloquer' onClick={() => quick(u, 'block')}><Ban size={16} color='#f71717' /></button>
                                        )}
                                    </span>
                                </div>
                            ))}
                            {!items.length && <Empty>Aucun user</Empty>}
                        </div>
                    </HScroll>
                    <Pager page={data.page} pages={data.pages} onPage={setPage} />
                </>
            )}

            {confirm && <Confirm {...confirm} onClose={() => setConfirm(null)} />}
        </div>
    )
}

export default UpUsers;
